import axios from 'axios';
import * as Sentry from '@sentry/nextjs';
import i18nConfig from '../i18nConfig';

const api = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
  timeout: 60_000,
  headers: { 'Content-Type': 'application/json', 'X-Client': 'main-front' },
});

// ── Las lecturas del navegador van sin X-Client ──
// Una cabecera propia vuelve "no simple" al pedido de CORS: antes de cada GET a la API
// el navegador mandaba un OPTIONS y esperaba la respuesta. Era una ida y vuelta más
// (0,5 s medidos en un celular con 4G lento) delante de los precios de /libros y del
// checkout, que sin precios no muestra nada. La cabecera solo etiqueta la columna
// `client` de api_request_logs, y LogApiRequest la deduce del Origin cuando falta.
// Content-Type no molesta: axios lo saca solo cuando el pedido no lleva cuerpo.
// Del lado del servidor (SSR) no hay CORS y la cabecera sigue yendo.
api.interceptors.request.use((config) => {
  if (typeof window !== 'undefined') {
    const method = String(config.method || 'get').toLowerCase();
    if (method === 'get' || method === 'head') config.headers?.delete?.('X-Client');
  }
  return config;
});

// ── Reintento acotado para requests seguras de reintentar ──
// Por defecto SOLO reintenta ante fallos de transporte (ERR_NETWORK): la request
// no llegó a completarse (CORS / conexión cortada / sin red / DNS), así que
// reintentar no puede duplicar efectos en el servidor. Para lecturas idempotentes
// se puede ampliar el predicado con `isRetriableRead` (suma 5xx).
// NO incluimos timeouts (ECONNABORTED) a propósito: el timeout global de axios es
// 60s, así que reintentar timeouts dejaría al usuario minutos en el loader; los
// "Network Error" reales fallan rápido (ERR_NETWORK), que es lo que queremos cubrir.
// IMPORTANTE: no envolver POSTs con efectos secundarios (cobros) salvo con
// `isTransportError`, y nunca reintentar respuestas HTTP de error en esos casos.
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const isTransportError = (err) =>
  err?.code === 'ERR_NETWORK' || err?.message === 'Network Error';

export const isRetriableRead = (err) =>
  isTransportError(err) ||
  (typeof err?.response?.status === 'number' && err.response.status >= 500);

export async function withRetry(
  fn,
  { retries = 2, baseDelay = 600, retryOn = isTransportError } = {},
) {
  let attempt = 0;
  for (;;) {
    try {
      return await fn();
    } catch (err) {
      if (attempt >= retries || !retryOn(err)) throw err;
      // Breadcrumb para no perder visibilidad de la flakiness de red: un retry que
      // se recupera no genera evento propio, pero queda en el rastro del proximo.
      try {
        Sentry.addBreadcrumb({
          category: 'http.retry',
          level: 'warning',
          message: `retry ${attempt + 1}/${retries}`,
          data: { code: err?.code, message: err?.message, status: err?.response?.status },
        });
      } catch {}
      await sleep(baseDelay * 2 ** attempt + Math.floor(Math.random() * 250));
      attempt += 1;
    }
  }
}

// ── Acceso revocado por distribución no autorizada ─────────────────────────
// El middleware del backend corta TODO lo privado (portal, descargas, apps) con
// 403 + error_code ACCESS_REVOKED, incluso con una sesión de Firebase ya abierta.
// Sin esto la persona se quedaría en un portal que falla en cada request sin saber
// por qué: la deslogueamos y la mandamos al login, que le muestra el cartel con los
// datos que dejamos acá.
export const REVOKED_STORAGE_KEY = 'bcm_revoked_notice';

// ── La sesión abierta no es de un comprador ────────────────────────────────
// Mismo problema de fondo que el de arriba, otra causa: `VerifyFirebaseToken` corta
// todo lo privado con 403 + NO_PURCHASE cuando el mail de la sesión no tiene ninguna
// compra. El caso real que lo destapó: alguien compró con un mail, entró al portal con
// otro, y a mitad de camino le reasignaron las ventas al primero. Su sesión quedó
// siendo la de un no comprador SIN que él hiciera nada.
//
// Sin esto la pantalla entraba en un pinponeo: el portal veía el 403 y mandaba a
// /login; el login veía el usuario de Firebase todavía abierto y lo mandaba de vuelta
// al portal. Ninguno de los dos deslogueaba ni decía nada. Medido en prod el 4-sep:
// 107 respuestas 403 en 37 segundos, la pantalla parpadeando, y la persona sin enterarse
// de que la salida era entrar con el otro mail.
export const NO_PURCHASE_STORAGE_KEY = 'bcm_no_purchase_notice';

// El login (/api/auth/*) queda afuera a propósito: ahí el 403 lo maneja la propia
// pantalla, y redirigir sería recargarla justo cuando acaba de pintar el cartel.
const AUTH_ENDPOINT = /\/auth\//;

// El redirect conserva el idioma del path. Sin prefijo, el proxy vuelve a elegirlo por
// la cookie `ln` (que solo existe si la persona tocó el selector de idioma) o por el
// Accept-Language del navegador: un alemán que entró por un aviso podía terminar
// leyendo el cartel en otro idioma que el de la página donde estaba.
function loginUrlDelIdiomaActual(conContinue = false) {
  const ln = window.location.pathname.split('/')[1];
  const base = i18nConfig.i18n.locales.includes(ln) ? `/${ln}/login` : '/login';
  if (!conContinue) return base;
  // Volver a donde estaba después de entrar con el mail correcto. El portal ya lo hacía
  // así, y perderlo sería un paso de más justo para el que ya pagó.
  const aqui = window.location.pathname + window.location.search;
  return `${base}?continue=${encodeURIComponent(aqui)}`;
}

function enLoginYa() {
  const partes = window.location.pathname.split('/').filter(Boolean);
  return partes[partes.length - 1] === 'login';
}

/**
 * Saca a la persona de lo privado dejándole un cartel para que sepa por qué.
 *
 * El signOut no es cosmético: sin él la sesión de Firebase sigue siendo "válida" en el
 * cliente hasta que expire el ID token (1 h), así que el login la vería logueada y la
 * devolvería al lugar del que la acabamos de sacar. Es la mitad del pinponeo.
 */
async function sacarDeLoPrivado(storageKey, payload, { conContinue = false } = {}) {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(payload));
  } catch {
    /* sessionStorage bloqueado: igual la sacamos del portal */
  }

  try {
    const [{ auth }, { signOut }] = await Promise.all([
      import('./firebase-client'),
      import('firebase/auth'),
    ]);
    if (auth) await signOut(auth);
  } catch {
    /* si falla el signOut igual redirigimos */
  }

  // Si ya está en el login no hay nada que redirigir: el cartel lo levanta la pantalla
  // del sessionStorage. Recargar acá sería pisar lo que la persona esté tipeando.
  if (!enLoginYa()) window.location.replace(loginUrlDelIdiomaActual(conContinue));
}

api.interceptors.response.use(undefined, async (error) => {
  try {
    if (
      typeof window !== 'undefined' &&
      error?.response?.status === 403 &&
      !AUTH_ENDPOINT.test(error?.config?.url || '')
    ) {
      const code = error.response.data?.error_code;

      if (code === 'ACCESS_REVOKED') {
        await sacarDeLoPrivado(REVOKED_STORAGE_KEY, error.response.data.revoked || {});
      } else if (code === 'NO_PURCHASE') {
        // Se mira el `error_code` y NUNCA el `error: 'forbidden'` pelado: ese string lo
        // devuelven también book3d, piano-roll y el clarinete cuando falta la
        // SUSCRIPCIÓN, y ahí quien lo recibe es un comprador bien logueado. Basta con
        // mirar los números de prod: el 96,8% de los 403 del sitio son de book3d y son
        // compradores sin suscripción. Deslogearlos sería romperle la sesión a la
        // inmensa mayoría para arreglarle el bucle a uno.
        await sacarDeLoPrivado(
          NO_PURCHASE_STORAGE_KEY,
          { email: error.response.data.email || '' },
          { conContinue: true },
        );
      }
    }
  } catch {
    /* nunca romper el manejo de errores por el interceptor */
  }

  return Promise.reject(error);
});

export default api;
